import {
  Badge,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  QueryStateBoundary,
} from "@/components/ui";
import { centsToUsd } from "@/lib/money";
import { useContact } from "@/api/hooks/useContacts";
import { useEnrollment } from "@/api/hooks/useClassRegistration";
import {
  ENROLLMENT_STATUS_BADGE_VARIANT,
  ENROLLMENT_STATUS_LABELS,
  PAYMENT_CHANNEL_LABELS,
} from "@/api/types/classRegistration";
import { formatBirthdate, formatConsentTimestamp } from "./classFormat";

interface EnrollmentDetailDrawerProps {
  enrollmentId: string | null;
  onClose: () => void;
}

/**
 * Enrollment detail drawer — modeled as a Dialog, this codebase's
 * drawer-equivalent (see features/bookings/BookingDetailDialog.tsx's own
 * comment on the same convention). The "Consent Audit Trail" section is a
 * COMPLIANCE feature (Texas single-skill-exemption — see
 * app/models/enrollment.py's module docstring): it surfaces all three
 * consent timestamps plus the exact wording version the guardian agreed
 * to, clearly formatted — never a raw ISO string dump.
 */
export function EnrollmentDetailDrawer({ enrollmentId, onClose }: EnrollmentDetailDrawerProps) {
  const { data: enrollment, isLoading, error, refetch } = useEnrollment(enrollmentId ?? undefined);
  const { data: guardian } = useContact(enrollment?.guardian_contact_id);

  return (
    <Dialog open={!!enrollmentId} onClose={onClose} ariaLabel="Enrollment detail">
      <DialogContent size="md">
        <DialogHeader onClose={onClose}>Enrollment detail</DialogHeader>
        <DialogBody className="space-y-5">
          <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
            {enrollment && (
              <>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-display text-lg font-semibold text-text-primary">
                      {enrollment.child_first_name} {enrollment.child_last_name}
                    </p>
                    <p className="text-sm text-text-secondary">Born {formatBirthdate(enrollment.child_birthdate)}</p>
                  </div>
                  <Badge variant={ENROLLMENT_STATUS_BADGE_VARIANT[enrollment.status]}>
                    {ENROLLMENT_STATUS_LABELS[enrollment.status]}
                  </Badge>
                </div>

                <div className="space-y-1 text-sm">
                  <p className="text-text-secondary">
                    Guardian: <span className="text-text-primary">{guardian?.name ?? "…"}</span>
                  </p>
                  {guardian?.email && <p className="text-text-secondary">Email: {guardian.email}</p>}
                  {guardian?.phone && <p className="text-text-secondary">Phone: {guardian.phone}</p>}
                </div>

                <div className="rounded-md bg-bg-hover px-3 py-2 text-sm">
                  <p className="text-text-secondary">
                    Payment channel: <span className="text-text-primary">{PAYMENT_CHANNEL_LABELS[enrollment.payment_channel]}</span>
                  </p>
                  <p className="text-text-secondary">
                    Price: <span className="font-semibold text-text-primary">{centsToUsd(enrollment.monthly_price_cents)}/mo</span>
                    {enrollment.base_price_cents !== enrollment.monthly_price_cents && (
                      <span className="ml-1 text-text-muted">(base {centsToUsd(enrollment.base_price_cents)}/mo)</span>
                    )}
                  </p>
                  {enrollment.sibling_discount_applied && (
                    <p className="text-text-secondary">Includes a 10% sibling discount.</p>
                  )}
                </div>

                <div>
                  <h3 className="mb-2 font-display text-sm font-semibold text-text-primary">Consent Audit Trail</h3>
                  <ul className="space-y-2 rounded-md border border-border px-3 py-3 text-sm" data-testid="consent-audit-trail">
                    <li className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-text-secondary">Liability waiver</span>
                      <span className="text-text-primary">
                        Acknowledged {formatConsentTimestamp(enrollment.waiver_at)}
                      </span>
                    </li>
                    <li className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-text-secondary">Statutory disclosure</span>
                      <span className="text-text-primary">
                        Acknowledged {formatConsentTimestamp(enrollment.disclosure_at)}
                      </span>
                    </li>
                    <li className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-text-secondary">Media release</span>
                      <span className="text-text-primary">
                        {enrollment.media_release === "granted" ? "Granted" : "Declined"}{" "}
                        {formatConsentTimestamp(enrollment.media_release_at)}
                      </span>
                    </li>
                    <li className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-text-secondary">Consent text version</span>
                      <span className="text-text-primary">v{enrollment.consent_text_version}</span>
                    </li>
                  </ul>
                </div>

                {enrollment.notes && (
                  <div>
                    <h3 className="mb-1 font-display text-sm font-semibold text-text-primary">Notes</h3>
                    <p className="text-sm text-text-secondary">{enrollment.notes}</p>
                  </div>
                )}
              </>
            )}
          </QueryStateBoundary>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
